import { FormEvent, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Alert, Button, CircularProgress } from "@mui/material";
import { CalendarMonth, CheckCircle, LocationOn, PersonSearch, Schedule, School } from "@mui/icons-material";

import {
  buscarDetalhePresencaLink,
  cadastrarInscreverMarcarPresencaLink,
  consultarCpfPresencaLink,
  ConsultaPresencaLinkResponse,
  DetalhePresencaLinkResponse,
  inscreverMarcarPresencaLink,
  marcarPresencaLink,
} from "API/apiPresencasLink";
import CadastroProfissionaisForm from "components/molecules/CadastroProfissionaisForm/CadastroProfissionaisForm";
import CadastroUsuarioForm from "components/molecules/CadastroUsuarioForm/CadastroUsuarioForm";
import ContatoForm from "components/molecules/ContatoForm/ContatoForm";
import EnderecoForm from "components/molecules/EnderecoForm/EnderecoForm";
import PessoalForm from "components/molecules/PessoalForm/PessoalForm";
import { CadastroPayload, CadastroProvider, useCadastro } from "resources/CadastroProvider";
import { isValidCPF } from "utils/DocumentValidators";
import { montarEnderecoTurma } from "../../../utils/turmaEndereco";

import styles from "./PresencaTurma.module.css";
import styles_button from "../../../styles/Button.module.css";
import styles_input from "../../../styles/Input.module.css";

type ConsultaData = ConsultaPresencaLinkResponse["data"];
type DetalheLinkData = DetalhePresencaLinkResponse["data"];
type Coordenadas = { latitude: string; longitude: string };
const LOCATION_REQUEST_ENABLED = false;

const apenasNumeros = (value: string) => value.replace(/\D/g, "");
const parametroHabilitado = (value: unknown) => value === true || value === 1;

const formatarCpfInput = (value: string) => {
  const cpf = apenasNumeros(value).slice(0, 11);
  return cpf
    .replace(/(\d{3})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d{1,2})$/, "$1-$2");
};

const dataAtualLocal = () => {
  const data = new Date();
  const ano = data.getFullYear();
  const mes = String(data.getMonth() + 1).padStart(2, "0");
  const dia = String(data.getDate()).padStart(2, "0");
  return `${ano}-${mes}-${dia}`;
};

const formatarDataBr = (data?: string | null) => {
  if (!data) return "-";
  const [ano, mes, dia] = data.split("-");
  if (!ano || !mes || !dia) return data;
  return `${dia}/${mes}/${ano}`;
};

const formatarPeriodo = (inicio?: string | null, fim?: string | null) => {
  if (!inicio && !fim) return "-";
  if (inicio === fim || !fim) return formatarDataBr(inicio);
  return `${formatarDataBr(inicio)} a ${formatarDataBr(fim)}`;
};

const formatarHora = (hora?: string | null) => (hora ? hora.slice(0, 5) : "-");

const obterLocalizacao = () => {
  return new Promise<Coordenadas>((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error("Seu navegador não permite obter localização."));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          latitude: String(position.coords.latitude),
          longitude: String(position.coords.longitude),
        });
      },
      () => reject(new Error("Não foi possível obter sua localização. Verifique a permissão no navegador.")),
      {
        enableHighAccuracy: true,
        timeout: 15000,
        maximumAge: 0,
      },
    );
  });
};

const CadastroPresencaLink = ({
  codigo,
  cpf,
  coordenadas,
  getLocalizacaoOpcional,
  onSuccess,
  onBack,
}: {
  codigo: string;
  cpf: string;
  coordenadas: Coordenadas | null;
  getLocalizacaoOpcional: () => Promise<Coordenadas | null>;
  onSuccess: () => void;
  onBack: () => void;
}) => {
  const handleSubmitCadastro = async (payload: CadastroPayload) => {
    const localizacao = coordenadas || (await getLocalizacaoOpcional());

    await cadastrarInscreverMarcarPresencaLink(codigo, {
      ...payload,
      userData: {
        ...payload.userData,
        CPF: apenasNumeros(payload.userData.CPF || cpf),
      },
      ...(localizacao
        ? {
            latitude: localizacao.latitude,
            longitude: localizacao.longitude,
          }
        : {}),
    });
  };

  return (
    <CadastroProvider
      basePath={null}
      initialCpf={cpf}
      onBackStart={onBack}
      onRegisterSubmit={handleSubmitCadastro}
      onRegisterSuccess={onSuccess}
    >
      <CadastroPresencaLinkContent />
    </CadastroProvider>
  );
};

const CadastroPresencaLinkContent = () => {
  const { activeStepIndex, steps, loadingRegister } = useCadastro();

  const stepComponents = [
    <CadastroUsuarioForm key="usuario" />,
    <PessoalForm key="pessoal" />,
    <ContatoForm key="contato" />,
    <EnderecoForm key="endereco" />,
    <CadastroProfissionaisForm key="profissionais" />,
  ];

  return (
    <div className={styles.registrationBox}>
      <div className={styles.steps}>
        {steps.map((step, index) => (
          <span
            key={step.label}
            className={index === activeStepIndex ? styles.stepActive : styles.step}
          >
            {index + 1}
          </span>
        ))}
      </div>
      {stepComponents[activeStepIndex]}
      {loadingRegister && (
        <div className={styles.loadingOverlay}>
          <CircularProgress size={26} />
          <span>Finalizando cadastro e presença...</span>
        </div>
      )}
    </div>
  );
};

const PresencaTurma = () => {
  const [searchParams] = useSearchParams();
  const codigo = searchParams.get("cod") || "";
  const [cpf, setCpf] = useState("");
  const [consulta, setConsulta] = useState<ConsultaData | null>(null);
  const [detalheLink, setDetalheLink] = useState<DetalheLinkData | null>(null);
  const [loadingConsulta, setLoadingConsulta] = useState(false);
  const [loadingDetalheLink, setLoadingDetalheLink] = useState(false);
  const [loadingLocalizacao, setLoadingLocalizacao] = useState(false);
  const [coordenadas, setCoordenadas] = useState<Coordenadas | null>(null);
  const [loadingPresenca, setLoadingPresenca] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [mostrarCadastro, setMostrarCadastro] = useState(false);
  const cadastroHabilitado = parametroHabilitado(detalheLink?.turma?.habilitar_cadastro);
  const presencaNaoInscritoHabilitada = parametroHabilitado(
    detalheLink?.turma?.habilitar_presenca_nao_inscrito,
  );

  const cpfNumeros = useMemo(() => apenasNumeros(cpf), [cpf]);
  const presencaJaMarcadaHoje = useMemo(() => {
    if (!consulta?.presencas) return false;
    const hoje = dataAtualLocal();
    return consulta.presencas.some(
      (presenca) => presenca.data_presenca === hoje && presenca.tipo === "PRESENTE",
    );
  }, [consulta]);

  useEffect(() => {
    const carregarDetalheLink = async () => {
      if (!codigo) {
        setError("Link de presença inválido.");
        return;
      }

      try {
        setLoadingDetalheLink(true);
        setError("");
        const response = await buscarDetalhePresencaLink(codigo);
        setDetalheLink(response.data);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Erro ao consultar dados da turma.");
      } finally {
        setLoadingDetalheLink(false);
      }
    };

    carregarDetalheLink();
  }, [codigo]);

  const getLocalizacaoOpcional = async () => {
    if (!LOCATION_REQUEST_ENABLED) {
      return null;
    }

    if (coordenadas) return coordenadas;

    try {
      setLoadingLocalizacao(true);
      const localizacao = await obterLocalizacao();
      setCoordenadas(localizacao);
      return localizacao;
    } catch {
      return null;
    } finally {
      setLoadingLocalizacao(false);
    }
  };

  const handleConsultarCpf = async (event: FormEvent) => {
    event.preventDefault();
    setError("");
    setSuccess("");
    setConsulta(null);
    setMostrarCadastro(false);

    if (!codigo) {
      setError("Link de presença inválido.");
      return;
    }

    if (!isValidCPF(cpfNumeros)) {
      setError("Informe um CPF válido.");
      return;
    }

    try {
      setLoadingConsulta(true);
      const response = await consultarCpfPresencaLink(codigo, cpf);
      setConsulta(response.data);

      const usuarioNaoCadastrado = response.data.status === "USUARIO_NAO_CADASTRADO";

      setMostrarCadastro(usuarioNaoCadastrado && cadastroHabilitado);

      if (usuarioNaoCadastrado && !cadastroHabilitado) {
        setError("Nao encontramos seu cadastro e esta turma nao permite cadastro de novos participantes por este link.");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erro ao consultar CPF.");
    } finally {
      setLoadingConsulta(false);
    }
  };

  const marcarPresenca = async () => {
    if (!codigo || !consulta) return;

    if (
      consulta.status === "USUARIO_NAO_MATRICULADO" &&
      !presencaNaoInscritoHabilitada
    ) {
      setError("Esta turma nao permite registrar presenca para participantes nao inscritos.");
      return;
    }

    if (presencaJaMarcadaHoje) {
      setError("Sua presença já foi marcada para hoje.");
      return;
    }

    try {
      setLoadingPresenca(true);
      setError("");
      setSuccess("");
      const localizacao = await getLocalizacaoOpcional();
      const payload = {
        cpf: cpfNumeros,
        ...(localizacao
          ? {
              latitude: localizacao.latitude,
              longitude: localizacao.longitude,
            }
          : {}),
      };

      if (consulta.status === "USUARIO_MATRICULADO") {
        await marcarPresencaLink(codigo, payload);
      } else if (consulta.status === "USUARIO_NAO_MATRICULADO") {
        await inscreverMarcarPresencaLink(codigo, payload);
      }

      setSuccess("Presença marcada com sucesso!");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erro ao marcar presença.");
    } finally {
      setLoadingPresenca(false);
    }
  };

  const getMensagemConsulta = () => {
    if (!consulta) return "";
    if (consulta.status === "USUARIO_MATRICULADO") {
      return `Olá, ${consulta.usuario?.nome || "participante"}. Você está inscrito nesta turma.`;
    }
    if (consulta.status === "USUARIO_NAO_MATRICULADO") {
      if (!presencaNaoInscritoHabilitada) {
        return `Olá, ${consulta.usuario?.nome || "participante"}. Esta turma não permite registrar presença para participantes não inscritos.`;
      }
      return `Olá, ${consulta.usuario?.nome || "participante"}. Você será inscrito na turma ao marcar presença.`;
    }
    if (!cadastroHabilitado) {
      return "Não encontramos seu cadastro e esta turma não permite cadastro de novos participantes por este link.";
    }
    return "Não encontramos seu cadastro. Preencha os dados abaixo para se cadastrar, inscrever e marcar presença.";
  };

  return (
    <main className={styles.page}>
      <section className={styles.card}>
        <div className={styles.header}>
          <CheckCircle />
          <div>
            <h1>Registro de Presença</h1>
            <p>Informe seu CPF para registrar presença na turma.</p>
          </div>
        </div>

        {loadingDetalheLink && (
          <div className={styles.infoLoading}>
            <CircularProgress size={22} />
            <span>Carregando dados da turma...</span>
          </div>
        )}

        {detalheLink && (
          <div className={styles.courseInfo}>
            <div className={styles.courseTitle}>
              <School />
              <div>
                <span>{detalheLink.acao.tipologia}</span>
                <strong>{detalheLink.acao.nome}</strong>
              </div>
            </div>

            <div className={styles.infoGrid}>
              <div className={styles.infoItem}>
                <span>Turma</span>
                <strong>{detalheLink.turma.nome}</strong>
              </div>
              <div className={styles.infoItem}>
                <span>Modalidade</span>
                <strong>{detalheLink.turma.modalidade || detalheLink.acao.modalidade}</strong>
              </div>
              <div className={styles.infoItem}>
                <CalendarMonth />
                <div>
                  <span>Período</span>
                  <strong>{formatarPeriodo(detalheLink.turma.data_inicio, detalheLink.turma.data_fim)}</strong>
                </div>
              </div>
              <div className={styles.infoItem}>
                <Schedule />
                <div>
                  <span>Horário</span>
                  <strong>{formatarHora(detalheLink.turma.hora_inicio)} às {formatarHora(detalheLink.turma.hora_fim)}</strong>
                </div>
              </div>
            </div>

            <div className={styles.addressInfo}>
              <LocationOn />
              <div>
                <span>{detalheLink.turma.link ? "Link" : "Endereço"}</span>
                <strong>{montarEnderecoTurma(detalheLink.turma) || detalheLink.turma.link || "-"}</strong>
              </div>
            </div>
          </div>
        )}

        <form className={styles.searchForm} onSubmit={handleConsultarCpf}>
          <div className={styles.inputGroup}>
            <label htmlFor="cpf-presenca" className={styles_input.titleInput}>
              CPF
            </label>
            <input
              id="cpf-presenca"
              className={`${styles_input.input} ${styles.cpfInput}`}
              value={cpf}
              onChange={(event) => setCpf(formatarCpfInput(event.target.value))}
              maxLength={14}
              placeholder="000.000.000-00"
            />
          </div>
          <Button
            type="submit"
            variant="contained"
            className={`${styles_button.buttonSquarePrimary} ${styles.searchButton}`}
            startIcon={loadingConsulta ? <CircularProgress size={16} /> : <PersonSearch />}
            disabled={loadingConsulta}
          >
            Consultar
          </Button>
        </form>

        {error && <Alert severity="error">{error}</Alert>}
        {success && <Alert severity="success">{success}</Alert>}
        {LOCATION_REQUEST_ENABLED && loadingLocalizacao && (
          <Alert severity="info">Obtendo localização...</Alert>
        )}
        {LOCATION_REQUEST_ENABLED && coordenadas && !loadingLocalizacao && (
          <Alert severity="success">Localização autorizada.</Alert>
        )}

        {consulta && (
          <div className={styles.resultBox}>
            <p>{getMensagemConsulta()}</p>

            {presencaJaMarcadaHoje && (
              <Alert severity="info">
                Sua presença já foi marcada para hoje. Não é possível marcar novamente.
              </Alert>
            )}

            {consulta.status !== "USUARIO_NAO_CADASTRADO" &&
              (consulta.status !== "USUARIO_NAO_MATRICULADO" ||
                presencaNaoInscritoHabilitada) && (
              <Button
                variant="contained"
                className={styles_button.buttonSquareSuccess}
                startIcon={loadingPresenca ? <CircularProgress size={16} /> : <LocationOn />}
                onClick={marcarPresenca}
                disabled={loadingPresenca || presencaJaMarcadaHoje || !!success}
              >
                Marcar presença
              </Button>
            )}
          </div>
        )}
      </section>

      {mostrarCadastro && (
        <section className={styles.cardWide}>
          <CadastroPresencaLink
            codigo={codigo}
            cpf={cpfNumeros}
            coordenadas={coordenadas}
            getLocalizacaoOpcional={getLocalizacaoOpcional}
            onBack={() => setMostrarCadastro(false)}
            onSuccess={() => {
              setMostrarCadastro(false);
              setSuccess("Cadastro realizado e presença marcada com sucesso!");
            }}
          />
        </section>
      )}
    </main>
  );
};

export default PresencaTurma;
